"use client";

import { useRef } from "react";
import Image from "next/image";
import { motion, useScroll, useSpring, useTransform } from "motion/react";
import { Github, Linkedin } from "lucide-react";
import { useViewportHeight } from "@/app/hooks/useViewportHeight";
import { springConfig } from "@/app/hooks/useSpringConfig";

const ACCENT_HIGHLIGHTS = [
  "individuelle Designs",
  "Liebe zum Detail",
  "100% DSGVO ohne Cookies",
  "Für jedes Budget",
] as const;

const SCROLL_CONFIG = {
  offset: [0, 1.02, 1.12, 1.45],
  liftValues: (vh: number) => [0, 0, -vh, -vh],
};

const SOCIAL_LINKS = [
  { href: "https://github.com/CreateYourReality", Icon: Github, label: "GitHub" },
  { href: "https://www.linkedin.com/in/timo-m%C3%B6wes-466b54286/", Icon: Linkedin, label: "LinkedIn" },
] as const;

const Landing = () => {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const viewportHeight = useViewportHeight();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  const landingLift = useTransform(
    scrollYProgress,
    SCROLL_CONFIG.offset,
    SCROLL_CONFIG.liftValues(viewportHeight),
    { clamp: false },
  );

  const landingLiftSmooth = useSpring(landingLift, springConfig.default);

  const easedProgress = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 22,
    mass: 0.35,
  });

  const sunY = useTransform(easedProgress, [0, 1], [0, 140]);
  const sunX = useTransform(easedProgress, [0, 1], [0, -200]);
  const sunRotate = useTransform(easedProgress, [0, 1], [0, 10]);
  const sunScale = useTransform(easedProgress, [0, 1], [1, 1.05]);

  const cloudOneX = useTransform(easedProgress, [0, 1], [0, -80]);
  const cloudOneY = useTransform(easedProgress, [0, 1], [0, 60]);
  const cloudTwoX = useTransform(easedProgress, [0, 1], [0, 90]);
  const cloudTwoY = useTransform(easedProgress, [0, 1], [0, 30]);
  const cloudThreeX = useTransform(easedProgress, [0, 1], [0, -60]);
  const cloudThreeY = useTransform(easedProgress, [0, 1], [0, 50]);
  const cloudFourX = useTransform(easedProgress, [0, 1], [0, 180]);
  const cloudFourY = useTransform(easedProgress, [0, 1], [0, -30]);
  const cloudFiveX = useTransform(easedProgress, [0, 1], [0, -120]);
  const cloudFiveY = useTransform(easedProgress, [0, 1], [0, 80]);
  const cloudSixX = useTransform(easedProgress, [0, 1], [0, 200]);
  const cloudSixY = useTransform(easedProgress, [0, 1], [0, 70]);

  const mistOpacity = useTransform(easedProgress, [0, 1], [0.85, 0.45]);
  const haloOpacity = useTransform(easedProgress, [0, 1], [0.55, 0.3]);

  // tint the sky towards #6EB1FD on scroll (stronger)
  const darkenOpacity = useTransform(easedProgress, [0, 1], [0, 0.95]);

  return (
    <motion.section
      ref={sectionRef}
      id="home"
      className="relative min-h-[180vh] w-full"
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="fixed inset-0 flex h-screen w-full items-center justify-center overflow-hidden pt-20 lg:pt-28 bg-gradient-to-b from-[#bfe6ff] via-[#8ccaff] to-[#66aafc]"
        style={{ y: landingLiftSmooth }}
      >
        {/* Cartoon sky elements */}
  <div className="pointer-events-none absolute inset-0 z-[6]">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ y: sunY, x: sunX, rotate: sunRotate, scale: sunScale }}
          className="absolute top-[45%] xs:top-17 left-1/2 xs:left-[15%] sm:left-62 -translate-x-1/2 xs:translate-x-0 h-36 w-36 rounded-full border-[6px] border-white/80 bg-[#ffe066]"
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.5, ease: "easeOut" }}
          style={{ x: cloudOneX, y: cloudOneY }}
          className="absolute top-[60%] xs:top-37 left-[1%] xs:left-[10%] sm:left-[24%] h-16 w-32 rounded-full border-[5px] border-white/85 bg-white shadow-[0px_8px_0px_rgba(0,0,0,0.1)]"
        >
          <div className="absolute -top-7 left-6 h-18 w-18 rounded-full bg-white" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.16, duration: 0.55, ease: "easeOut" }}
          style={{ x: cloudTwoX, y: cloudTwoY }}
          className="absolute top-[70%] xs:top-77 right-[5%] xs:right-auto xs:-left-[2%] sm:-left-[2%] h-14 w-35 rounded-full border-[5px] border-white/85 bg-white shadow-[0px_8px_0px_rgba(0,0,0,0.1)]"
        >
          <div className="absolute -top-7 left-6 h-18 w-18 rounded-full bg-white" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.55, ease: "easeOut" }}
          style={{ x: cloudThreeX, y: cloudThreeY }}
          className="absolute top-[25%] xs:top-48 left-[5%] xs:left-[35%] sm:left-1/2 h-16 w-36 xs:translate-x-0 sm:-translate-x-1/2 rounded-full border-[5px] border-white/90 bg-white shadow-[0px_10px_0px_rgba(0,0,0,0.12)]"
        >
          <div className="absolute -top-8 left-6 h-22 w-22 rounded-full bg-white" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.26, duration: 0.55, ease: "easeOut" }}
          style={{ x: cloudFourX, y: cloudFourY }}
          className="absolute bottom-[10%] xs:bottom-76 right-[10%] xs:right-[15%] sm:right-[15%] h-12 w-28 rounded-full border-[5px] border-white/85 bg-white shadow-[0px_8px_0px_rgba(0,0,0,0.1)]"
        >
          <div className="absolute -top-6 left-3 h-16 w-16 rounded-full bg-white" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.82 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.6, ease: "easeOut" }}
          style={{ x: cloudFiveX, y: cloudFiveY }}
          className="absolute top-35 -left-[5%] sm:left-[3%] h-22 w-60 rounded-full border-[5px] border-white/90 bg-white shadow-[0px_10px_0px_rgba(0,0,0,0.12)] hidden xs:block"
        >
          <div className="absolute -top-14 left-8 h-29 w-29 rounded-full bg-white" />
          <div className="absolute -top-10 left-22 h-29 w-29 rounded-full bg-white" />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.84 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.34, duration: 0.6, ease: "easeOut" }}
          style={{ x: cloudSixX, y: cloudSixY }}
          className="absolute top-[35%] xs:top-42 sm:top-42 right-[2%] xs:right-66 sm:right-66 h-18 w-44 rounded-full border-[5px] border-white/85 bg-white shadow-[0px_8px_0px_rgba(0,0,0,0.1)]"
        >
          <div className="absolute -top-9 left-8 h-25 w-25 rounded-full bg-white" />
        </motion.div>

        <motion.div
          style={{ opacity: mistOpacity }}
          className="absolute inset-x-0 bottom-[18%] h-24 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.5),transparent_65%)]"
        />
        <motion.div
          style={{ opacity: haloOpacity }}
          className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.35),transparent_55%)]"
        />
        </div>

        {/* subtle darken overlay driven by scroll (covers background + sky elements but stays behind content) */}
        <motion.div
          aria-hidden
          className="absolute inset-0 pointer-events-none z-[5] bg-[#6EB1FD]"
          style={{ opacity: darkenOpacity }}
        />

        <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col gap-8 px-2.5 xs:px-3 py-12 lg:flex-row lg:items-center lg:gap-12 lg:px-16 lg:py-24 xl:px-20">
        <motion.div
          initial={{ opacity: 0, y: 36, rotate: -1.5 }}
          animate={{ opacity: 1, y: 0, rotate: 0 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 140, damping: 20 }}
          className="flex w-full flex-1 flex-col text-[#1d2b4f]"
        >
          <motion.span
            initial={{ opacity: 0, y: -28, scale: 0.7, rotate: -18 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.18, duration: 0.35, ease: "easeOut" }}
            className="landing-hola absolute z-[-1] left-0 sm:left-4 top-10 sm:top-20 lg:left-10 lg:top-28 mb-2 inline-flex w-fit items-center gap-0 rounded-2xl border-[2px] xs:border-[3px] border-[#1d2b4f] bg-[#fef3c7] px-2 py-1 xs:px-3 xs:py-1.5 lg:px-4 lg:py-2 font-luckiest text-sm xs:text-base lg:text-lg uppercase tracking-[0.15em] xs:tracking-[0.2em] text-[#1d2b4f] shadow-[3px_3px_0_#1d2b4f] xs:shadow-[4px_4px_0_#1d2b4f] opacity-92 rotate-[8deg] xs:-rotate-8"
          >
            HOLA!
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, type: "spring", stiffness: 150, damping: 22 }}
            className="leading-text font-luckiest stroke-text text-3xl-extra leading-tight text-[#FFE066] [text-shadow:3px_3px_0_#1d2b4f] sm:text-5xl lg:text-7xl lg:[text-shadow:4px_4px_0_#1d2b4f] mt-4 sm:mt-0"
          >
            Webdesign & Entwicklung
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28, type: "spring", stiffness: 140, damping: 22 }}
            className="landing-subtext ml-1 mt-1 sm:mt-2 max-w-xl font-[Poppins] text-[18px] xs:text-base font-bold leading-[1.1] text-white lg:ml-2 lg:text-3xl lg:leading-[1]"
            style={{
              textShadow: '2px 2px 4px rgba(29, 43, 79, 0.9), 0 0 25px rgba(255, 224, 102, 0.35), 0 4px 12px rgba(0, 0, 0, 0.3)',
              WebkitTextStroke: '0.5px rgba(29, 43, 79, 0.3)'
            }}
          >
            Einzigartige Webseiten statt Baukasten-Einheitsbrei – <span className="">modern, sicher, individuell. </span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.36, type: "spring", stiffness: 170, damping: 18 }}
            className="flex flex-row flex-wrap gap-2 pt-3 lg:pt-2"
          >
            <motion.a
              whileHover={{ y: -6, rotate: -1 }}
              whileTap={{ scale: 0.96 }}
              href="#kontakt"
              className="inline-flex items-center justify-center gap-2 rounded-sm border-[3px] lg:border-[4px] border-[var(--dark-blue)] bg-[var(--yellow)] px-2 py-1 xs:py-2 lg:px-4 lg:py-2.5 text-sm lg:text-base font-[Poppins] font-bold text-[var(--dark-blue)] shadow-[2px_2px_0_var(--dark-blue)] lg:shadow-[3px_3px_0_var(--dark-blue)] transition-colors duration-200 hover:bg-[var(--light-blue)] hover:text-white"
            >
<div className="whitespace-normal">
  <span className="hidden sm:inline">Jetzt&nbsp;</span>Kontakt<span className="hidden sm:inline">&nbsp;aufnehmen</span>!
</div>            </motion.a>
            <motion.a
              whileHover={{ y: -6, rotate: 1 }}
              whileTap={{ scale: 0.96 }}
              href="#arbeiten"
              className="inline-flex items-center justify-center gap-2 rounded-sm border-[3px] lg:border-[4px] border-[var(--dark-blue)] bg-[#F6F1CB] px-2 py-1 xs:py-2 lg:px-4 lg:py-2.5 text-sm lg:text-base font-[Poppins] font-bold text-[var(--dark-blue)] shadow-[2px_2px_0_var(--dark-blue)] lg:shadow-[3px_3px_0_var(--dark-blue)] transition-colors duration-200 hover:bg-[var(--beige)]"
            >
              Arbeiten ansehen
            </motion.a>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              hidden: {},
              visible: {
                transition: { staggerChildren: 0.07, delayChildren: 0.46 },
              },
            }}
            className="flex flex-wrap gap-2 lg:gap-3 pt-3 lg:pt-4"
          >
            {ACCENT_HIGHLIGHTS.map((item) => (
              <motion.span
                key={item}
                variants={{
                  hidden: { opacity: 0, y: 18, scale: 0.9 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: { type: "spring", stiffness: 240, damping: 18 },
                  },
                }}
                className="landing-highlight-badge inline-flex items-center gap-1 xs:gap-1.5 lg:gap-2 rounded-full border-[2px] lg:border-[3px] border-[#1d2b4f] bg-white/80 px-1.5 py-1 xs:px-2.5 xs:py-1.5 lg:px-4 lg:py-2 text-[8px] xs:text-[10px] lg:text-xs font-[Poppins] font-semibold uppercase tracking-wide text-[#1d2b4f] shadow-[2px_2px_0_#1d2b4f] lg:shadow-[4px_4px_0_#1d2b4f]"
              >
                <motion.span
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                  className="h-1 w-1 xs:h-1.5 xs:w-1.5 lg:h-2.5 lg:w-2.5 rounded-full bg-[#5BA0FF]"
                />
                {item}
              </motion.span>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, rotate: 1.4 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 120, damping: 18 }}
            className="relative mt-2 sm:mt-6 flex justify-center lg:hidden"
          >
            <div className="landing-moewe-container relative h-[172px] w-[172px] xs:h-48 xs:w-48 sm:h-64 sm:w-64">
              <div className="absolute inset-0 rounded-full border-[4px] sm:border-[6px] border-[#1d2b4f] bg-[#ffeecd] shadow-[6px_8px_0_#1d2b4f] sm:shadow-[10px_12px_0_#1d2b4f]" />
              <div className="absolute inset-3 sm:inset-4 rounded-full border-[3px] sm:border-[4px] border-white bg-[#ffeecd]" />
              <Image
                src="/img/möwe.webp"
                alt="Möwe am Laptop"
                fill
                priority
                fetchPriority="high"
                className="relative z-10 object-contain drop-shadow-[0_12px_0_rgba(29,43,79,0.35)] sm:drop-shadow-[0_18px_0_rgba(29,43,79,0.35)]"
              />
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9, rotate: -1.8 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 120, damping: 20 }}
          className="relative hidden flex-1 items-center justify-center lg:flex"
        >
          <div className="relative h-[30rem] w-[30rem] max-w-full">
            <div className="absolute inset-0 rounded-[3.5rem] border-[8px] border-[#1d2b4f] shadow-[16px_18px_0_#1d2b4f]" style={{ background: 'var(--gradient-beige-diagonal)' }} />
            <div className="absolute inset-6 rounded-[2.8rem] border-[5px] border-white bg-[#ffeecd]" />
            <motion.div
              animate={{ rotate: [0, 1, 0, -1, 0] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-4 -right-6 h-20 w-20 rounded-full border-[6px] border-[#1d2b4f] bg-[#ff7eb6] shadow-[4px_4px_0_#1d2b4f]"
            />
            <motion.div
              animate={{ y: [0, -5, 0, -3, 0], rotate: [0, 1, -0.5, 1, 0] }}
              transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-5 left-[-18px] z-[100] h-20 w-44 rounded-2xl border-[6px] border-[var(--dark-blue)] bg-[var(--cyan)] shadow-[6px_8px_0_var(--dark-blue)] flex items-center justify-center gap-3 px-4"
            >
              {SOCIAL_LINKS.map(({ href, Icon, label }) => (
                <motion.a
                  key={href}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.15, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center justify-center w-12 h-12 rounded-full bg-white border-[3px] border-[var(--dark-blue)] shadow-[2px_2px_0_var(--dark-blue)] hover:shadow-[3px_3px_0_var(--dark-blue)] transition-shadow"
                  title={label}
                >
                  <Icon size={22} className="text-[var(--dark-blue)]" strokeWidth={2.5} />
                </motion.a>
              ))}
            </motion.div>

            <motion.div
              animate={{ y: [0, -5, 0, -3, 0], rotate: [0, 1, -0.5, 1, 0] }}
              transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
              className="relative h-full w-full"
            >
              <Image
                src="/img/möwe.webp"
                alt="Möwe am Laptop"
                fill
                priority
                fetchPriority="high"
                className="relative z-10 object-contain"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: -30, scale: 0.85 }}
              animate={{ opacity: 1, y: -14, scale: 1 }}
              transition={{ delay: 0.26, duration: 0.4, ease: "easeOut" }}
              className="absolute -top-6 left-1/2 flex -translate-x-1/2 items-center gap-3 rounded-lg border-[4px] border-[#1d2b4f] bg-white px-6 py-1 text-sm font-[Poppins] font-bold text-[#1d2b4f] shadow-[6px_6px_0_#1d2b4f]"
            >
              <span className="relative flex h-3 w-3">
                <motion.span
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: [0, 1.15, 1] }}
                  transition={{ delay: 0.32, duration: 0.5, ease: "easeOut" }}
                  className="absolute inset-0 rounded-lg bg-[#5BA0FF]/70"
                />
                <span className="relative inline-flex h-3 w-3 rounded-lg bg-[#5BA0FF]" />
              </span>
              Hol das Beste aus deiner Marke raus!
            </motion.div>
          </div>
        </motion.div>
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 w-full select-none">
          <Image
            src="/img/beach.svg"
            alt="Strand-Silhouette"
            width={1920}
            height={300}
            className="w-full rotate-180"
            priority
            fetchPriority="high"
          />
        </div>
      </motion.div>
    </motion.section>
  );
};

export default Landing;
